export default class {
    constructor ($translate, lodash, toastr, ModalService, TokenService, PrivilegeService, QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM, QUESTIONS_TYPE_ENUM) {
        this.$translate = $translate;
        this.lodash = lodash;
        this.toastr = toastr;
        this.ModalService = ModalService;
        this.PrivilegeService = PrivilegeService;
        this.defaultLanguage = TokenService.getCompanyLanguage();
        this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM = QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM;
        this.QUESTIONS_TYPE_ENUM = QUESTIONS_TYPE_ENUM;
    }

    // this check is performed for question related settings as well (question pools, question categories etc)
    canManageQuestionRelatedData () {
        // allow to manage for super and company admin
        if (this.PrivilegeService.isCompanyAdmin()) {
            return true;
        }

        // allow to manage if company access is all can manage
        return this.PrivilegeService.isSiteAccessForAllUsers();
    }

    /**
     * modified form is returned back
     * @param form
     * @param scale
     * @returns {*}
     */
    assignScaleFieldsBeforeSave (form, scale) {
        if (!form.model.id) {
            // always delete old answer before save when on create
            form.model.questionAnswers = [];
        }

        // assign scale.list to question answers
        this.lodash.forEach(scale.list, (answer, key) => {
            if (!form.model.questionAnswers[key]) {
                form.model.questionAnswers.push({ translations: {} });
            }
            if (!form.model.questionAnswers[key].hasOwnProperty('translations')) {
                form.model.questionAnswers[key].translations = {};
            }
            if (!form.model.questionAnswers[key].translations[this.defaultLanguage]) {
                form.model.questionAnswers[key].translations[this.defaultLanguage] = {};
            }
            form.model.questionAnswers[key].translations[this.defaultLanguage].text = answer.label;
            form.model.questionAnswers[key].correctness = this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.CORRECT;
            form.model.questionAnswers[key].text = answer.label;
        });

        // delete all translations if they were deleted from scale.list
        for (let i = scale.list.length; i < form.model.questionAnswers.length; i++) {
            if (form.model.questionAnswers[i].correctness !== this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.NOT_APPLICABLE) {
                form.model.questionAnswers.splice(i, 1);
            }
        }

        // allow N/A option assigning logic
        if (form.model.allowNAOption && form.model.questionAnswers[form.model.questionAnswers.length - 1].correctness
            !== this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.NOT_APPLICABLE) {
            if (!form.model.questionAnswers[form.model.questionAnswers.length]) {
                form.model.questionAnswers.push({ translations: {} });
            }
            const lastIndex = form.model.questionAnswers.length - 1;
            if (!form.model.questionAnswers[lastIndex].translations[this.defaultLanguage]) {
                form.model.questionAnswers[lastIndex].translations[this.defaultLanguage] = {};
            }
            form.model.questionAnswers[lastIndex].translations[this.defaultLanguage].text = 'N/A';
            form.model.questionAnswers[lastIndex].correctness
                = this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.NOT_APPLICABLE;
            form.model.questionAnswers[lastIndex].points = null;
        }

        if (!form.model.allowNAOption && form.model.questionAnswers[form.model.questionAnswers.length - 1].correctness
            === this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.NOT_APPLICABLE) {
            delete form.model.questionAnswers.splice(form.model.questionAnswers.length - 1, 1);
        }
        return form;
    }

    assignMatrixFieldsBeforeSave (form, matrix) {
        if (form.model.id) {
            // map existing translations to questionI18ns object
            if (form.model.childQuestionsActiveI18ns) {
                this.lodash.forEach(form.model.childQuestions, (childQuestion, key) => {
                    this.lodash.forEach(form.model.childQuestionsActiveI18ns, (childQuestionTranslation) => {
                        if (childQuestionTranslation.questionId === childQuestion.id) {
                            if (!form.model.childQuestions[key].questionI18ns) {
                                form.model.childQuestions[key].questionI18ns = {};
                            }
                            if (!form.model.childQuestions[key].questionI18ns[childQuestionTranslation.language]) {
                                form.model.childQuestions[key].questionI18ns[childQuestionTranslation.language] = {};
                            }
                            form.model.childQuestions[key].questionI18ns[childQuestionTranslation.language] = childQuestionTranslation;
                        }
                    });
                });
            }
        }

        // assign scale.list to question answers
        this.lodash.forEach(matrix.list, (childQuestion, key) => {
            if (!form.model.childQuestions) {
                form.model.childQuestions = [];
            }
            if (!form.model.childQuestions[key]) {
                form.model.childQuestions[key] = {};
            }
            if (!form.model.childQuestions[key].questionI18ns) {
                form.model.childQuestions[key].questionI18ns = {};
            }
            if (!form.model.childQuestions[key].questionI18ns[this.defaultLanguage]) {
                form.model.childQuestions[key].questionI18ns[this.defaultLanguage] = {};
            }
            if (!form.model.childQuestions[key].externalId) {
                form.model.childQuestions[key].externalId = form.model.externalId ? `CHILD-${form.model.externalId}-${key}` : '';
            }

            form.model.childQuestions[key].questionI18ns[this.defaultLanguage].name = childQuestion.label;

            // assign FREE_TEXT type for childQuestions to kill answer validation
            form.model.childQuestions[key].type = this.QUESTIONS_TYPE_ENUM.KEYS.FREE_TEXT;
        });

        // delete all translations if they were deleted from matrix.list
        for (let i = matrix.list.length; i < form.model.childQuestions.length; i++) {
            form.model.childQuestions.splice(i, 1);
        }

        return form;
    }

    delete (question) {
        return question.$delete({ id: question.id, validateOnly: true })
            .then(() => {
                const confirmMessage = question.formQuestions && question.formQuestions.length
                    ? this.$translate.instant(
                        'question.Question is connected to a form. If question will be deleted, it will be removed from form also. Are you sure that you would like to delete this question?'
                    )
                    : this.$translate.instant('question.Are you sure that you would like to delete this question?')
                ;
                return this.ModalService.delete(confirmMessage)
                    .then(() => question.$delete({ id: question.id }))
                    .then(() => {
                        this.toastr.success(this.$translate.instant('question.Question deleted'));
                    });
            })
            .catch((errors) => {
                const assessmentWarning = this.lodash.find(errors.data, (error) => error.field === 'ASSESSMENT_VIRTUAL_ATTR');
                if (assessmentWarning) {
                    return this.ModalService.delete(assessmentWarning.message)
                        .then(() => question.$delete({ id: question.id }))
                        .then(() => {
                            this.toastr.success(this.$translate.instant('question.Question deleted'));
                        });
                }
                return this.toastr.error(this.$translate.instant('question.Question delete failed'));
            })
        ;
    }

    addScaleValue (scale, value, label) {
        scale.list.push({ label: label ? label.toString() : '', value });
    }

    addMatrixValue (matrix, label = '') {
        matrix.list.push({ label: (label ? label.toString() : '') });
    }

    addItem (models) {
        // Add a new item to the models list
        models.list.push({});

        // Create a default answer with correctness set to INCORRECT
        return {
            correctness: this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.INCORRECT,
            isCorrect: this.QUESTIONS_ANSWER_CORRECTNESS_TYPE_ENUM.KEYS.INCORRECT,
            isNotApplicable: '',
            translations: {}
        };
    }

    getImageUrl (question, thumbnailDir) {
        let baseUrl = `${SEED_CORE.ENV.API_URL}questionImages/${question.companyId}/${question.id}`;
        if (thumbnailDir) {
            baseUrl = `${baseUrl}/thumbnails/${thumbnailDir}`;
        }
        return `${baseUrl}/${question.imageFilename}`;
    }

    getVideoUrl (question) {
        const baseUrl = `${SEED_CORE.ENV.API_URL}questionVideos/${question.companyId}/${question.id}`;
        return `${baseUrl}/${question.videoFilename}`;
    }

    getAnswerImageUrl (answer, companyId, thumbnailDir) {
        let baseUrl = `${SEED_CORE.ENV.API_URL}questionAnswerImages/${companyId}/${answer.id}`;
        if (thumbnailDir) {
            baseUrl = `${baseUrl}/thumbnails/${thumbnailDir}`;
        }
        return `${baseUrl}/${answer.imageFilename}`;
    }

    getAnswerVideoUrl (answer, companyId) {
        const baseUrl = `${SEED_CORE.ENV.API_URL}questionAnswerVideos/${companyId}/${answer.id}`;
        return `${baseUrl}/${answer.videoFilename}`;
    }

    openImageModal (question) {
        const url = this.getImageUrl(question);
        return this.ModalService.open({
            template: `
                <modals-image
                    url="vm.url"
                    uib-modal-instance="vm.$uibModalInstance"
                    />
                `,
            size: 'large',
            controller ($translate, $uibModalInstance) {
                this.$uibModalInstance = $uibModalInstance;
                this.url = url;
            }
        });
    }

    openAnswerImageModal (answer, companyId) {
        const url = this.getAnswerImageUrl(answer, companyId);
        return this.ModalService.open({
            template: `
                <modals-image
                    url="vm.url"
                    uib-modal-instance="vm.$uibModalInstance"
                    />
                `,
            size: 'large',
            controller ($translate, $uibModalInstance) {
                this.$uibModalInstance = $uibModalInstance;
                this.url = url;
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/services/question/index.js